<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: null,
  },
  pipeline: {
    type: Object,
    required: true,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  getDealValue: {
    type: Function,
    required: true,
  },
  getDealStatus: {
    type: Function,
    required: true,
  },
  formatDateTime: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();

const leadName = ref('');
const leadEmail = ref('');
const leadPhone = ref('');
const leadAvatarUrl = ref('');
const leadAvatarFile = ref(null);
const selectedStageId = ref('');
const selectedAgentId = ref('');
const selectedInboxId = ref('');
const dealValue = ref(0);
const dealStatus = ref('open');
const leadSource = ref('');
const entryDateTime = ref('');

// UTMs & Tracking
const utmSource = ref('');
const utmMedium = ref('');
const utmCampaign = ref('');
const utmTerm = ref('');
const utmContent = ref('');
const gclid = ref('');
const fbclid = ref('');

const showUtmFields = ref(false);

const SOURCE_OPTIONS = [
  { value: 'Google Ads', label: 'Google Ads (Pesquisa / Display)' },
  { value: 'Meta Ads', label: 'Meta Ads (Facebook / Instagram)' },
  { value: 'Instagram', label: 'Instagram (Direct / Perfil)' },
  { value: 'Google Meu Negócio', label: 'Google Meu Negócio (Maps)' },
  { value: 'TikTok Ads', label: 'TikTok Ads' },
  { value: 'Orgânico', label: 'Orgânico (SEO / Blog)' },
  { value: 'Indicação', label: 'Indicação' },
  { value: 'Direto', label: 'Direto' },
  { value: 'Outras Origens', label: 'Outras Origens' },
];

function syncFormWithConversation() {
  if (!props.conversation) return;

  const conv = props.conversation;
  const contact = conv.meta?.sender || {};
  const customAttrs = conv.custom_attributes || {};

  leadName.value = contact.name || '';
  leadEmail.value = contact.email || '';
  leadPhone.value = contact.phone_number || '';
  leadAvatarUrl.value = contact.thumbnail || contact.avatar_url || '';
  leadAvatarFile.value = null;

  // Stage
  const labels = conv.labels || [];
  const stages = props.pipeline?.stages || [];
  const matchedStage = stages.find(
    s =>
      (s.label && labels.includes(s.label)) ||
      (s.id !== undefined &&
        (labels.includes(s.id) ||
          labels.includes(String(s.id)) ||
          labels.includes(`stage_${s.id}`)))
  );
  selectedStageId.value =
    matchedStage?.id !== undefined
      ? matchedStage.id
      : stages[0]?.id !== undefined
        ? stages[0].id
        : '';

  // Agent & Inbox
  selectedAgentId.value = conv.meta?.assignee?.id ? String(conv.meta.assignee.id) : '';
  selectedInboxId.value = conv.inbox_id ? String(conv.inbox_id) : '';

  // Deal Value & Status
  dealValue.value = props.getDealValue(conv) || 0;
  dealStatus.value = props.getDealStatus(conv) || 'open';

  // Source & Tracking
  leadSource.value =
    customAttrs.utm_source ||
    customAttrs.channel_origin ||
    customAttrs.ads_source ||
    (customAttrs.gclid ? 'Google Ads' : customAttrs.fbclid ? 'Meta Ads' : 'Sem Origem');

  utmSource.value = customAttrs.utm_source || '';
  utmMedium.value = customAttrs.utm_medium || '';
  utmCampaign.value = customAttrs.utm_campaign || '';
  utmTerm.value = customAttrs.utm_term || '';
  utmContent.value = customAttrs.utm_content || '';
  gclid.value = customAttrs.gclid || '';
  fbclid.value = customAttrs.fbclid || '';

  // Entry Date/Time
  const createdTimestamp = conv.created_at || Math.floor(Date.now() / 1000);
  entryDateTime.value = props.formatDateTime(createdTimestamp);
}

watch(
  () => props.conversation,
  () => {
    syncFormWithConversation();
  },
  { immediate: true }
);

watch(leadSource, (newVal) => {
  if (newVal) {
    utmSource.value = newVal;
  }
});

function handleAvatarUpload(event) {
  const [file] = event.target.files;
  if (file) {
    leadAvatarFile.value = file;
    leadAvatarUrl.value = URL.createObjectURL(file);
  }
}

function handleSave() {
  if (!props.conversation) return;

  const finalSource = leadSource.value || utmSource.value || 'Sem Origem';

  const updatedData = {
    conversationId: props.conversation.id,
    contactId: props.conversation.meta?.sender?.id,
    name: leadName.value.trim(),
    email: leadEmail.value.trim(),
    phone_number: leadPhone.value.trim(),
    avatarFile: leadAvatarFile.value,
    stageId: selectedStageId.value,
    agentId: selectedAgentId.value || null,
    inboxId: selectedInboxId.value ? Number(selectedInboxId.value) : props.conversation.inbox_id,
    dealValue: parseFloat(dealValue.value) || 0,
    dealStatus: dealStatus.value,
    source: finalSource,
    customAttributes: {
      ...(props.conversation.custom_attributes || {}),
      deal_value: parseFloat(dealValue.value) || 0,
      deal_status: dealStatus.value,
      utm_source: finalSource,
      utm_medium: utmMedium.value,
      utm_campaign: utmCampaign.value,
      utm_term: utmTerm.value,
      utm_content: utmContent.value,
      gclid: gclid.value,
      fbclid: fbclid.value,
      channel_origin: finalSource,
      ads_source: finalSource,
    },
  };

  emit('save', updatedData);
  emit('close');
}
</script>

<template>
  <div
    v-if="isOpen && conversation"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
    @click.self="emit('close')"
  >
    <div
      class="flex flex-col w-full max-w-xl max-h-[90vh] rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl overflow-hidden"
    >
      <!-- Modal Header -->
      <div
        class="flex items-center justify-between border-b border-n-weak px-6 py-4 bg-n-surface-1/60"
      >
        <div class="flex items-center gap-2.5">
          <div
            class="flex size-9 items-center justify-center rounded-xl bg-woot-500/10 text-woot-500 border border-woot-500/20"
          >
            <Icon icon="i-lucide-edit-3" class="size-5" />
          </div>
          <div>
            <h2 class="text-sm font-extrabold text-n-slate-12">
              Editar Oportunidade / Lead
            </h2>
            <p class="text-xs text-n-slate-10">
              Personalize os detalhes do card no Kanban e as informações do Lead.
            </p>
          </div>
        </div>

        <button
          type="button"
          class="p-1.5 rounded-lg text-n-slate-10 hover:bg-n-surface-2 hover:text-n-slate-12 transition-colors cursor-pointer"
          @click="emit('close')"
        >
          <Icon icon="i-lucide-x" class="size-5" />
        </button>
      </div>

      <!-- Modal Body (Scrollable) -->
      <div class="flex flex-1 flex-col gap-4 overflow-y-auto p-6 custom-scrollbar">
        <!-- Lead Identity (Avatar & Name) -->
        <div class="flex items-center gap-4 rounded-xl border border-n-weak bg-n-surface-1 p-3.5">
          <div class="relative group/avatar flex-shrink-0">
            <Avatar
              :name="leadName || 'Contato'"
              :src="leadAvatarUrl"
              size="56"
              class="rounded-full border border-n-weak shadow-sm object-cover"
            />
            <label
              class="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer text-xs font-semibold"
              title="Trocar Foto"
            >
              <Icon icon="i-lucide-camera" class="size-4" />
              <input
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleAvatarUpload"
              />
            </label>
          </div>

          <div class="flex flex-col flex-1 gap-1 min-w-0">
            <label class="text-[11px] font-bold text-n-slate-11">Nome do Lead</label>
            <input
              v-model="leadName"
              type="text"
              placeholder="Ex: Paulo Santos"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-xs font-bold text-n-slate-12 focus:border-woot-500 focus:outline-none"
            />
          </div>
        </div>

        <!-- Two Columns: Canal (Inbox) & Fonte (Origin) -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <!-- Canal / Inbox -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Canal de Atendimento</label>
            <select
              v-model="selectedInboxId"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-xs font-semibold text-n-slate-12 focus:border-woot-500 focus:outline-none cursor-pointer"
            >
              <option v-for="ib in inboxes" :key="ib.id" :value="String(ib.id)">
                {{ ib.name }} ({{ ib.channel_type }})
              </option>
            </select>
          </div>

          <!-- Fonte / Origem -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Origem do Lead (Fonte)</label>
            <select
              v-model="leadSource"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-xs font-semibold text-n-slate-12 focus:border-woot-500 focus:outline-none cursor-pointer"
            >
              <option
                v-for="opt in SOURCE_OPTIONS"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Two Columns: Etapa do Funil & Atendente -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <!-- Etapa do Funil -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Etapa no Funil</label>
            <select
              v-model="selectedStageId"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-xs font-semibold text-n-slate-12 focus:border-woot-500 focus:outline-none cursor-pointer"
            >
              <option
                v-for="stg in pipeline.stages"
                :key="stg.id"
                :value="stg.id"
              >
                {{ stg.name }}
              </option>
            </select>
          </div>

          <!-- Atendente Responsável -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Atendente / Responsável</label>
            <select
              v-model="selectedAgentId"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-xs font-semibold text-n-slate-12 focus:border-woot-500 focus:outline-none cursor-pointer"
            >
              <option value="">Não atribuído</option>
              <option
                v-for="ag in agents"
                :key="ag.id"
                :value="String(ag.id)"
              >
                {{ ag.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Two Columns: Valor do Negócio (R$) & Status do Negócio -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <!-- Deal Value (R$) -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Valor do Negócio (R$)</label>
            <input
              v-model="dealValue"
              type="number"
              step="0.01"
              placeholder="0,00"
              class="w-full h-[38px] rounded-lg border border-n-weak bg-n-solid-1 px-3 text-xs font-bold text-n-slate-12 focus:border-woot-500 focus:outline-none shadow-2xs"
            />
          </div>

          <!-- Deal Status (Open / Won / Lost) -->
          <div class="flex flex-col gap-1">
            <label class="text-[11px] font-bold text-n-slate-11">Status do Negócio</label>
            <div class="grid grid-cols-3 gap-1.5 h-[38px]">
              <button
                type="button"
                class="flex items-center justify-center rounded-lg text-xs font-bold border transition-colors cursor-pointer"
                :class="[
                  dealStatus === 'open'
                    ? 'bg-blue-500 text-white border-blue-600 shadow-2xs'
                    : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:bg-n-surface-2',
                ]"
                @click="dealStatus = 'open'"
              >
                Aberto
              </button>
              <button
                type="button"
                class="flex items-center justify-center rounded-lg text-xs font-bold border transition-colors cursor-pointer"
                :class="[
                  dealStatus === 'won'
                    ? 'bg-emerald-500 text-white border-emerald-600 shadow-2xs'
                    : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/20',
                ]"
                @click="dealStatus = 'won'"
              >
                ✓ Ganho
              </button>
              <button
                type="button"
                class="flex items-center justify-center rounded-lg text-xs font-bold border transition-colors cursor-pointer"
                :class="[
                  dealStatus === 'lost'
                    ? 'bg-rose-500 text-white border-rose-600 shadow-2xs'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-600 hover:bg-rose-500/20',
                ]"
                @click="dealStatus = 'lost'"
              >
                ✕ Perdido
              </button>
            </div>
          </div>
        </div>

        <!-- Entry Date & Time (Readonly Info) -->
        <div class="flex items-center justify-between rounded-lg bg-n-surface-2/60 px-3.5 py-2 border border-n-weak text-xs text-n-slate-11">
          <span class="flex items-center gap-1.5 font-medium">
            <Icon icon="i-lucide-calendar" class="size-3.5 text-[#84CC16]" />
            Data e Hora de Entrada do Lead:
          </span>
          <span class="font-bold text-n-slate-12">{{ entryDateTime || 'Agora' }}</span>
        </div>

        <!-- UTMs & Tracking Accordion -->
        <div class="flex flex-col rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden">
          <button
            type="button"
            class="flex items-center justify-between px-4 py-2.5 text-xs font-bold text-n-slate-12 hover:bg-n-surface-2 transition-colors cursor-pointer"
            @click="showUtmFields = !showUtmFields"
          >
            <div class="flex items-center gap-2">
              <Icon icon="i-lucide-target" class="size-4 text-purple-500" />
              <span>Parâmetros de Rastreamento (UTMs & Ads Tracking)</span>
            </div>
            <Icon
              :icon="showUtmFields ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
              class="size-4 text-n-slate-10"
            />
          </button>

          <div v-if="showUtmFields" class="flex flex-col gap-3 p-4 border-t border-n-weak bg-n-solid-1">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">UTM Source</label>
                <input
                  v-model="utmSource"
                  type="text"
                  placeholder="ex: google, facebook"
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">UTM Medium</label>
                <input
                  v-model="utmMedium"
                  type="text"
                  placeholder="ex: cpc, story, feed"
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">UTM Campaign</label>
                <input
                  v-model="utmCampaign"
                  type="text"
                  placeholder="ex: lancamento_agosto"
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">UTM Content</label>
                <input
                  v-model="utmContent"
                  type="text"
                  placeholder="ex: video_01"
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">Google Click ID (GCLID)</label>
                <input
                  v-model="gclid"
                  type="text"
                  placeholder="gclid_..."
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-bold text-n-slate-10">Meta Click ID (FBCLID)</label>
                <input
                  v-model="fbclid"
                  type="text"
                  placeholder="fbclid_..."
                  class="rounded border border-n-weak bg-n-surface-1 px-2.5 py-1 text-xs text-n-slate-12 focus:border-woot-500 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div
        class="flex items-center justify-end gap-2.5 border-t border-n-weak px-6 py-3.5 bg-n-surface-1/60"
      >
        <button
          type="button"
          class="rounded-xl border border-n-weak px-4 py-2 text-xs font-bold text-n-slate-11 hover:bg-n-surface-2 transition-colors cursor-pointer"
          @click="emit('close')"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="flex items-center gap-1.5 rounded-xl bg-woot-500 hover:bg-woot-600 px-5 py-2 text-xs font-bold text-white shadow-sm transition-all hover:shadow-md cursor-pointer"
          @click="handleSave"
        >
          <Icon icon="i-lucide-check" class="size-4" />
          <span>Salvar Alterações</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(150, 150, 150, 0.2);
  border-radius: 3px;
}
</style>
